<template>
  <SectionCard :id="section.id" :title="section.title">
    <div class="grid gap-6">
      <!-- Lånedelar -->
      <div :id="section.subSections.loanParts.id">
        <h3 class="mb-2">
          {{ section.subSections.loanParts.title }}
        </h3>

        <AppTable v-if="application.loansBreakdown.length">
          <thead>
            <tr>
              <th>Räntebindningstid</th>
              <th>Ränta</th>
              <th>Effektiv ränta</th>
              <th>Belopp</th>
              <th>Amortering</th>
              <th>Rabatt</th>
              <th>Tillägg</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="(ld, index) in application.loansBreakdown" :key="index">
              <AppTableCell>{{ $t(`INTEREST_TERM.${ld.interestTerm}`) }}</AppTableCell>
              <AppTableCell>{{ formatPercentage(ld.interestRate) }}</AppTableCell>
              <AppTableCell>{{ formatPercentage(ld.effectiveInterestRate) }}</AppTableCell>
              <AppTableCell>{{ formatAmountWithCurrency(ld.amount) }}</AppTableCell>
              <AppTableCell>{{ formatAmountWithCurrency(ld.amortization) }}</AppTableCell>
              <AppTableCell>
                <template v-if="ld.discounts?.length">
                  <div v-for="(discount, discountIndex) in ld.discounts" :key="`${discountIndex}-${discount.bps}`">
                    {{ discount.bps }} punkter
                    <div v-if="discount.reason" class="text-subtle text-xs">{{ discount.reason }}</div>
                  </div>
                </template>
                <span v-else>-</span>
              </AppTableCell>
              <AppTableCell>
                <span
                  v-if="ld.code?.includes('green')"
                  class="green-tag inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                >
                  Grönt
                </span>
                <span v-else>-</span>
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Inga lånedelar.</p>
      </div>

      <!-- Betalningsdetaljer -->
      <div :id="section.subSections.paymentDetails.id">
        <h3 class="mb-2">
          {{ section.subSections.paymentDetails.title }}
        </h3>

        <AppTable>
          <thead>
            <tr>
              <th>Fakturamottagare</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow>
              <AppTableCell>{{ invoiceRecipient }}</AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </div>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { SectionCard, AppTable, AppTableRow, AppTableCell } from '@staff/ui'
import { formatAmountWithCurrency, formatPercentage } from '@staff/shared'
import { getFullName } from '@/utils/applicationDisplay'
import { useRequiredApplication } from '@/views/application/application.context'
import type { ApplicationSections } from './sections'

defineProps<{
  section: ApplicationSections['loanParts']
}>()

const application = useRequiredApplication()

const invoiceRecipient = computed(() => {
  const recipient = application.value.applicants.find((a) => a.roles.isInvoiceRecipient)
  if (!recipient) return 'Saknar fakturamottagare'
  return getFullName(recipient)
})
</script>

<style scoped lang="scss">
.green-tag {
  background-color: var(--color-success-subtle);
  color: var(--color-success-on-subtle);
}
</style>
